import {
  ApplicationTypeKey,
  FrameworkKey,
  ProgrammingLanguageKey,
  TopicKey,
} from "@hub/data/values/data-keys.enum";
import { DocumentCardData } from "@hub/models/document/document-card-data";
import { DocumentFilterGroups } from "@hub/models/filters/document-filter-groups";
import { filterContent } from "@hub/services/filtering/content-filtering.utils";
import { StateService } from "@hub/services/state.service";
import { debounceTime, Observable, Subject } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

export interface InitPayload {
  query: {
    topic?: string;
    type?: string;
    framework?: string;
    language?: string;
    searchTerm?: string;
  };
  contentCardData: DocumentCardData[];
}

interface FilteringState {
  typeFilterValues: string[];
  frameworkFilterValues: string[];
  languageFilterValues: string[];
  featureFilterValues: string[];
  verticalFilterValues: string[];
  topicFilterValues: string[];
  searchTermFilterValue: string;
  enabledFilterGroups: {
    topic: boolean;
    type: boolean;
    framework: boolean;
    language: boolean;
  } | null;
  isModalVisible: boolean;
  content: DocumentCardData[] | null;
  results: DocumentCardData[] | null;
  filterOptions: DocumentFilterGroups | null;
}

interface FilterOptions {
  topic: boolean;
  type: boolean;
  framework: boolean;
  language: boolean;
}

const initialState: FilteringState = {
  typeFilterValues: [],
  frameworkFilterValues: [],
  languageFilterValues: [],
  topicFilterValues: [],
  featureFilterValues: [],
  verticalFilterValues: [],
  isModalVisible: false,
  content: null,
  results: null,
  enabledFilterGroups: null,
  searchTermFilterValue: "",
  filterOptions: null,
};

class _ContentFilterService extends StateService<FilteringState> {
  isModalVisible$: Observable<boolean> = this.select(
    (state) => state.isModalVisible
  );

  filterOptions$: Observable<DocumentFilterGroups | null> = this.select(
    (state) => state.filterOptions
  );

  typeFilterValues$: Observable<string[]> = this.select(
    (state) => state.typeFilterValues
  );

  frameworkFilterValues$: Observable<string[]> = this.select(
    (state) => state.frameworkFilterValues
  );

  languageFilterValues$: Observable<string[]> = this.select(
    (state) => state.languageFilterValues
  );

  featureFilterValues$: Observable<string[]> = this.select(
    (state) => state.featureFilterValues
  );

  verticalFilterValues$: Observable<string[]> = this.select(
    (state) => state.verticalFilterValues
  );

  topicFilterValues$: Observable<string[]> = this.select(
    (state) => state.topicFilterValues
  );

  searchTermFilterValue$: Observable<string> = this.select(
    (state) => state.searchTermFilterValue
  );

  hasSelectedFilters$: Observable<boolean> = this.select(
    (state) =>
      state.typeFilterValues.length > 0 ||
      state.frameworkFilterValues.length > 0 ||
      state.languageFilterValues.length > 0
  );

  results$: Observable<DocumentCardData[] | null> = this.select((state) =>
    filterContent({
      content: state.content,
      type: state.typeFilterValues,
      framework: state.frameworkFilterValues,
      language: state.languageFilterValues,
      topic: state.topicFilterValues,
      searchTerm: state.searchTermFilterValue,
    })
  );

  searchDebounceTimeMs = 300;
  searchTermSubject$ = new Subject<string>();

  constructor() {
    super(initialState);

    this.searchTermSubject$
      .pipe(debounceTime(this.searchDebounceTimeMs), distinctUntilChanged())
      .subscribe((nextValue: string) => {
        this.setSearchTermFilter(nextValue);
      });
  }

  pushSearchTerm = (nextValue: string) => {
    this.searchTermSubject$.next(nextValue);
  };

  clearAllFilterValues = () => {
    this.setState({
      typeFilterValues: [],
      frameworkFilterValues: [],
      languageFilterValues: [],
      featureFilterValues: [],
      verticalFilterValues: [],
    });
  };

  closeModal = () => {
    this.setState({ isModalVisible: false });
  };

  openModal = () => {
    this.setState({ isModalVisible: true });
  };

  setFilterOptions = (filterOptions: DocumentFilterGroups | null) => {
    if (filterOptions) {
      this.setState({ filterOptions });
    }
  };

  setEnabledFilterGroups = (options: FilterOptions) => {
    this.setState({ enabledFilterGroups: options });
  };

  getEnabledFilterGroups = () => {
    return this.state.enabledFilterGroups;
  };

  setSearchTermFilter = (term: string) => {
    this.setState({ searchTermFilterValue: term });
  };

  initializeFilterGroups = (payload: InitPayload) => {
    const { query, contentCardData } = payload;

    const topic = query.topic as TopicKey;
    const type = query.type as ApplicationTypeKey;
    const framework = query.framework as FrameworkKey;
    const language = query.language as ProgrammingLanguageKey;
    const searchTerm = query.searchTerm as string;

    const filters: {
      topic: string[];
      type: string[];
      framework: string[];
      language: string[];
      searchTerm: string;
    } = {
      topic: topic ? topic.split(",") : [],
      type: type ? type.split(",") : [],
      framework: framework ? framework.split(",") : [],
      language: language ? language.split(",") : [],
      searchTerm: searchTerm,
    };

    this.setState({
      content: filterContent({
        content: contentCardData,
        type: filters.type,
        framework: filters.framework,
        language: filters.language,
        searchTerm: filters.searchTerm,
        topic: filters.topic,
      }),
      typeFilterValues: filters.type,
      languageFilterValues: filters.language,
      frameworkFilterValues: filters.framework,
      searchTermFilterValue: filters.searchTerm,
      topicFilterValues: filters.topic,
    });
  };
}

export const ContentFilteringService = new _ContentFilterService();
